/**
 * useSearch — custom hook encapsulating the search fetch lifecycle.
 *
 * WI-19 changes:
 *  - Module-scope SWR cache (Map<string, SearchResult[]>, max 10 entries, insertion-order eviction).
 *  - Cache hit: paint immediately, fire background refetch silently (no skeleton).
 *  - Cache miss: loading skeleton path unchanged.
 *  - AbortController per submit call: rapid filter changes cancel in-flight fetches.
 *  - serializeFilters imported from api/search — single source of truth for cache key.
 *
 * WI-24 changes:
 *  - Expose clearResults() — sets rawResults to [] without touching the SWR cache.
 *    Called by SearchPage.handleFiltersChange when the user interactively changes a filter.
 *
 * Manages:
 *  - Loading and error state.
 *  - Client-side sort (score DESC or name ASC) on the loaded result set
 *    (per design: sort does not trigger a new API call).
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { SearchFilters, SearchResult, searchSchools, serializeFilters } from '../api/search';

export type SortMode = 'score' | 'name';

// ── SWR module-scope cache ────────────────────────────────────────────────────
// Cleared on hard reload (module-scope). Capped at 10 entries (insertion-order eviction).

const SWR_CACHE_MAX = 10;
const swrCache: Map<string, SearchResult[]> = new Map();

/**
 * Write a cache entry. Evicts the oldest entry when the cap is exceeded.
 * Uses insertion-order eviction (Map preserves insertion order in JS).
 */
function cacheWrite(key: string, results: SearchResult[]): void {
  if (swrCache.has(key)) {
    // Refresh entry: delete + re-insert to move to newest position
    swrCache.delete(key);
  }
  swrCache.set(key, results);
  if (swrCache.size > SWR_CACHE_MAX) {
    // Evict oldest (first inserted) entry
    const oldestKey = swrCache.keys().next().value;
    if (oldestKey !== undefined) {
      swrCache.delete(oldestKey);
    }
  }
}

/** Exposed for testing — clears the cache between test runs. */
export function clearSwrCache(): void {
  swrCache.clear();
}

/** Exposed for testing — read cache size. */
export function getSwrCacheSize(): number {
  return swrCache.size;
}

/** Exposed for testing — check if a key is present. */
export function swrCacheHas(key: string): boolean {
  return swrCache.has(key);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseSearchResult {
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  resultCount: number;
  sort: SortMode;
  setSort: (mode: SortMode) => void;
  submitSearch: (filters: SearchFilters) => void;
  /** WI-24: Clear rawResults from local state only. SWR cache is NOT invalidated. */
  clearResults: () => void;
}

export function useSearch(): UseSearchResult {
  const [rawResults, setRawResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortMode>('score');

  // Track the latest submit request via a counter to discard stale responses.
  const requestCounterRef = useRef(0);
  // Hold the current AbortController so we can cancel it on rapid changes.
  const abortControllerRef = useRef<AbortController | null>(null);

  const submitSearch = useCallback(async (filters: SearchFilters) => {
    // Sport is the only required filter; zero-position requests trigger SUM mode (item 14a).
    if (!filters.sport) {
      setRawResults([]);
      return;
    }

    // Cancel any in-flight fetch from a previous call.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Increment counter — this call "owns" the current generation.
    const generation = ++requestCounterRef.current;

    const cacheKey = serializeFilters(filters);
    const cached = swrCache.get(cacheKey);

    if (cached !== undefined) {
      // Cache hit: paint immediately, no skeleton.
      setRawResults(cached);
      setError(null);
      setLoading(false);

      // Fire background refetch silently.
      searchSchools(filters, controller.signal)
        .then((data) => {
          // Only commit if this is still the latest generation.
          if (generation !== requestCounterRef.current) return;
          cacheWrite(cacheKey, data);
          setRawResults(data);
        })
        .catch((err) => {
          // Background errors are swallowed — cached results remain shown.
          // AbortError is normal (newer call cancelled this one).
          if (err?.name === 'AbortError') return;
          // Log to console only — no UI disruption.
          console.warn('[useSearch] background refetch failed:', err);
        });
    } else {
      // Cache miss: foreground fetch with skeleton.
      setLoading(true);
      setError(null);

      try {
        const data = await searchSchools(filters, controller.signal);
        // Discard stale result if a newer call has been made.
        if (generation !== requestCounterRef.current) return;
        cacheWrite(cacheKey, data);
        setRawResults(data);
      } catch (err) {
        if (generation !== requestCounterRef.current) return;
        // AbortError means a newer call superseded this one — not a user-visible error.
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'An error occurred');
        setRawResults([]);
      } finally {
        if (generation === requestCounterRef.current) {
          setLoading(false);
        }
      }
    }
  }, []);

  // WI-24: Clear local result state only. SWR cache is intentionally NOT touched so
  // that re-submitting the same filters hits the cache (AC-7, TC-24-U10).
  const clearResults = useCallback(() => {
    setRawResults([]);
  }, []);

  // Client-side sort — no additional API call (per design spec).
  const results = useMemo(() => {
    if (sort === 'name') {
      return [...rawResults].sort((a, b) =>
        a.school_name.localeCompare(b.school_name)
      );
    }
    // Default: score DESC. Match the API's OQ-26-1 Option A ordering — for fallback
    // rows (has_data === false), use fallback_score.score in place of score (which is 0).
    // Without this branch, all fallback schools collapse to 0 and sort below real-data
    // rows regardless of their fallback magnitude — a regression of WI-26.
    const getScore = (r: SearchResult) => {
      if (r.has_data === false && r.fallback_score) return r.fallback_score.score;
      return r.score ?? r.total_score ?? 0;
    };
    return [...rawResults].sort((a, b) => getScore(b) - getScore(a));
  }, [rawResults, sort]);

  return {
    results,
    loading,
    error,
    resultCount: results.length,
    sort,
    setSort,
    submitSearch,
    clearResults,
  };
}
